/**
 * RefQuantityEditModal — Radix Dialog for editing a single reference quantity
 * from the DAG. Provides a number input and slider for value adjustment,
 * plus a mandatory "reason for change" field for provenance auditing.
 */

import { useEffect, useState } from "react";
import { Button, Dialog, Flex, Slider, Text, TextField } from "@radix-ui/themes";
import type { DAGNode } from "../../api/artifacts";
import { useUpsertReferenceQuantity, useRefQuantityHistory } from "../../hooks/useReferenceQuantities";
import { useBranchGuard } from "../../context/BranchGuardContext";
import { useRefAreaPreview } from "../../context/RefAreaPreviewContext";
import { useQueryClient } from "@tanstack/react-query";
import { ParameterHistoryStrip } from "../panels/ParameterHistoryStrip";
import { useTheme } from "../../context/ThemeContext";

const AREA_PARAMS = new Set(["wing_area", "ref_area", "s_gross", "s_net"]);

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  branch: string;
  quantity: DAGNode;
  geometryId: string | null;
}

function extractParam(node: DAGNode): string {
  return String(node.path).split(".").pop() ?? "?";
}

function sliderRange(current: number): [number, number] {
  if (current === 0) return [0, 100];
  const magnitude = Math.abs(current);
  return [0, magnitude * 2];
}

export function RefQuantityEditModal({
  open,
  onClose,
  projectId,
  branch,
  quantity,
  geometryId,
}: Props) {
  const currentValue = (quantity.value_scalar as number) ?? 0;
  const units = (quantity.units as string) ?? "";
  const param = extractParam(quantity);

  const [value, setValue] = useState(currentValue);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [min, max] = sliderRange(currentValue);

  const upsert = useUpsertReferenceQuantity(projectId, branch);
  const { guard } = useBranchGuard();
  const { setPreviewRefArea } = useRefAreaPreview();
  const qc = useQueryClient();
  const { theme } = useTheme();
  const isAreaParam = AREA_PARAMS.has(param);
  const { data: history } = useRefQuantityHistory(quantity.id, projectId, branch);
  const [selectedHistoryValues] = useState<Set<number>>(() => new Set());

  function updateValue(v: number) {
    setValue(v);
    if (isAreaParam) setPreviewRefArea(v);
  }

  function clearPreview() {
    if (isAreaParam) setPreviewRefArea(null);
  }

  useEffect(() => {
    if (open) {
      setValue(currentValue);
      setReason("");
      setError(null);
      if (isAreaParam) setPreviewRefArea(currentValue);
    } else {
      clearPreview();
    }
  }, [open, currentValue]);

  function handleSave() {
    if (!reason.trim()) {
      setError("A reason for change is required");
      return;
    }

    const payload = {
      project_id: projectId,
      parameter: param,
      value,
      units: units || undefined,
      geometry_id: geometryId ?? undefined,
      change_reason: reason.trim(),
    };

    clearPreview();
    onClose();

    guard(() => {
      upsert.mutate(payload, {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: ["provenance"] });
          qc.invalidateQueries({ queryKey: ["ref-quantity-history"] });
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : String(err));
        },
      });
    }, { hint: param });
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) { clearPreview(); onClose(); } }}>
      <Dialog.Content maxWidth="440px">
        <Dialog.Title>
          Edit {param}
          {units ? ` (${units})` : ""}
        </Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Current value: {currentValue}
          {units ? ` ${units}` : ""}
        </Dialog.Description>

        <Flex direction="column" gap="3" mt="4">
          <label>
            <Text size="2" weight="medium" mb="1" asChild>
              <span>Value</span>
            </Text>
            <TextField.Root
              type="number"
              value={String(value)}
              onChange={(e) => updateValue(Number(e.target.value))}
            />
          </label>

          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">Slider</Text>
            <Slider
              min={min}
              max={max}
              step={max > 100 ? 1 : 0.01}
              value={[value]}
              onValueChange={([v]) => updateValue(v ?? value)}
            />
            <Flex justify="between">
              <Text size="1" color="gray">{min}</Text>
              <Text size="1" color="gray">{max}</Text>
            </Flex>
          </Flex>

          {history && history.length >= 2 && (
            <ParameterHistoryStrip
              history={history}
              currentValue={currentValue}
              selectedValues={selectedHistoryValues}
              onToggleValue={(v) => updateValue(v)}
              units={units || null}
              isLight={theme === "light"}
            />
          )}

          <label>
            <Text size="2" weight="medium" mb="1" asChild>
              <span>Reason for change</span>
            </Text>
            <TextField.Root
              placeholder="e.g. Updated from wind tunnel data"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
            />
          </label>

          {error && (
            <Text size="1" color="red">{error}</Text>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? "Saving..." : "Save"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
